import type { FeedbackIdSource } from '~/shared/utils/feedback-input';

export const FEEDBACK_ID_ATTR = 'data-feedback-id';
export const FEEDBACK_UI_ATTR = 'data-feedback-ui';

export interface FeedbackTarget {
  element: Element;
  feedbackId: string;
  source: FeedbackIdSource;
}

const BLOCK_TAGS = new Set([
  'ARTICLE',
  'ASIDE',
  'DIALOG',
  'FIELDSET',
  'FIGURE',
  'FOOTER',
  'FORM',
  'HEADER',
  'MAIN',
  'NAV',
  'OL',
  'SECTION',
  'TABLE',
  'UL',
]);

const BLOCK_ROLES = new Set([
  'banner',
  'complementary',
  'dialog',
  'form',
  'grid',
  'list',
  'main',
  'navigation',
  'region',
  'table',
  'tabpanel',
]);

const BLOCK_CLASSES = [
  'MuiAccordion-root',
  'MuiCard-root',
  'MuiDialog-paper',
  'MuiDrawer-paper',
  'MuiPaper-root',
  'MuiTableContainer-root',
];

const MAX_PATH_SEGMENTS = 10;
const STABLE_ID = /^[A-Za-z][\w-]*$/;

function isBlock(el: Element): boolean {
  if (BLOCK_TAGS.has(el.tagName)) {
    return true;
  }
  const role = el.getAttribute('role');
  if (role && BLOCK_ROLES.has(role)) {
    return true;
  }
  return BLOCK_CLASSES.some((cls) => el.classList.contains(cls));
}

function isDocumentRoot(el: Element): boolean {
  return el.tagName === 'BODY' || el.tagName === 'HTML';
}

function segmentFor(el: Element): string {
  const tag = el.tagName.toLowerCase();
  if (el.id && STABLE_ID.test(el.id)) {
    return `${tag}#${el.id}`;
  }
  const parent = el.parentElement;
  if (!parent) {
    return tag;
  }
  const sameTag = Array.from(parent.children).filter(
    (sibling) => sibling.tagName === el.tagName
  );
  if (sameTag.length < 2) {
    return tag;
  }
  return `${tag}:nth-of-type(${sameTag.indexOf(el) + 1})`;
}

export function cssPath(el: Element): string {
  const segments: string[] = [];
  let node: Element | null = el;
  while (node && !isDocumentRoot(node) && segments.length < MAX_PATH_SEGMENTS) {
    const segment = segmentFor(node);
    segments.unshift(segment);
    if (segment.includes('#')) {
      break;
    }
    node = node.parentElement;
  }
  return segments.join(' > ') || el.tagName.toLowerCase();
}

export function findFeedbackTarget(
  start: Element | null
): FeedbackTarget | null {
  if (!start || isDocumentRoot(start)) {
    return null;
  }
  if (start.closest(`[${FEEDBACK_UI_ATTR}]`)) {
    return null;
  }

  const tagged = start.closest(`[${FEEDBACK_ID_ATTR}]`);
  const taggedId = tagged?.getAttribute(FEEDBACK_ID_ATTR)?.trim();
  if (tagged && taggedId) {
    return { element: tagged, feedbackId: taggedId, source: 'attribute' };
  }

  let node: Element | null = start;
  while (node && !isDocumentRoot(node)) {
    if (isBlock(node)) {
      return { element: node, feedbackId: cssPath(node), source: 'css-path' };
    }
    node = node.parentElement;
  }
  return { element: start, feedbackId: cssPath(start), source: 'css-path' };
}
